"use client";

/**
 * Guards for DOM lookups that must mean "this element is on screen right now",
 * not merely "this element exists somewhere in the document".
 *
 * Next 16 does not unmount the route you navigate away from. It keeps the
 * subtree mounted inside a hidden React `<Activity>` boundary so state and
 * scroll position survive going back, which means the previous page — and any
 * portalled dialog, drawer or overlay it had open — is still in the document,
 * still matched by `document.querySelector`, and still carrying whatever
 * `data-state="open"` attributes it had when you left. Activity only sets
 * `display: none`.
 *
 * Any logic that treats a successful query as proof of visibility will
 * therefore keep acting on the page the user already left.
 *
 * See node_modules/next/dist/docs/01-app/02-guides/preserving-ui-state.md
 * ("Testing" covers the same hazard for e2e selectors).
 */

/**
 * Whether `node` is actually laid out, i.e. not inside a `display: none`
 * subtree (which is how Activity hides a backgrounded route).
 *
 * Uses client rects rather than `offsetParent`, which is always null for the
 * `position: fixed` elements most overlays use, and rather than
 * `checkVisibility()`, which is not available on older WebKit.
 */
export function isRenderedElement(node: Element | null | undefined): boolean {
  if (!node?.isConnected) return false;
  return node.getClientRects().length > 0;
}

/**
 * `document.querySelector` that skips matches inside hidden Activity subtrees.
 *
 * Prefer scoping a query to a ref you own where that is possible; reach for
 * this when the target is portalled to `<body>` and out of reach of any ref.
 */
export function queryRenderedSelector(selector: string): Element | null {
  for (const node of document.querySelectorAll(selector)) {
    if (isRenderedElement(node)) return node;
  }
  return null;
}
